import React, { useMemo } from 'react'
import { Stack, Box, Typography, alpha, useTheme, Divider } from '@mui/material'
import { CheckCircle, HourglassEmpty, Error } from '@mui/icons-material'

import { PaymentHistory, PaymentMethodEnum, PaymentStatus, PaymentLabels } from '@/types/Payment'
import { numericUtils } from '@/utils/numeric'
import { timeUtils } from '@/utils/time'

type StatusColor = 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'

type TransactionConfig = {
  label: string
  color: StatusColor
  icon: React.ElementType
}

const STATUS_CONFIG: Record<PaymentStatus, TransactionConfig> = {
  COMPLETED: { label: 'Concluído', color: 'success', icon: CheckCircle },
  PENDING: { label: 'Pendente', color: 'warning', icon: HourglassEmpty },
  FAILED: { label: 'Falhou', color: 'error', icon: Error },
  CANCELED: { label: 'Cancelado', color: 'error', icon: Error },
}

type AmountLabels = { external: string; wallet: string }

// Só COMPLETED representa dinheiro que saiu. Em PENDING o valor ainda é uma
// intenção de cobrança, e em FAILED/CANCELED nada foi capturado pelo gateway —
// mostrar "Pago:" nesses casos faz o cliente achar que foi cobrado a cada tentativa.
const AMOUNT_LABELS: Record<PaymentStatus, AmountLabels | null> = {
  COMPLETED: { external: 'Pago:', wallet: 'Saldo:' },
  PENDING: { external: 'A pagar:', wallet: 'Saldo:' },
  FAILED: null,
  CANCELED: null,
}

export interface PaymentItemProps {
  transaction: PaymentHistory
}

export const PaymentItem: React.FC<PaymentItemProps> = ({ transaction }) => {
  const theme = useTheme()

  const statusConfig = STATUS_CONFIG[transaction.status] || {
    label: 'Desconhecido',
    color: 'grey',
    icon: Error,
  }

  const StatusIcon = statusConfig.icon
  const methodLabel = PaymentLabels[transaction.method as PaymentMethodEnum] || 'Desconhecido'
  const amountLabels = AMOUNT_LABELS[transaction.status] ?? null
  const amountColor = useMemo(() => {
    if (!AMOUNT_LABELS[transaction.status]) return 'text.disabled'
    return transaction.method === PaymentMethodEnum.CREDITS ? 'error.main' : 'text.primary'
  }, [transaction.status, transaction.method])

  const formattedMainValue = useMemo(() => {
    const formattedAmount = numericUtils.formatNumberToBRLCurrency(transaction.value)
    return transaction.method === PaymentMethodEnum.CREDITS ? `-${formattedAmount}` : formattedAmount
  }, [transaction.value, transaction.method])

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
        paddingX: 2.5,
        paddingY: 1.5,
        width: '100%',
        minHeight: 90,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="start">
        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette[statusConfig.color].main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              minWidth: 48,
            }}
          >
            <StatusIcon color={statusConfig.color} sx={{ fontSize: 24 }} />
          </Box>

          <Stack flex={1} overflow="hidden">
            <Typography variant="subtitle2" fontWeight="bold" noWrap>
              {statusConfig.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {timeUtils.formatDateWithTime(transaction.created_at)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Método: {methodLabel}
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="subtitle2" fontWeight="bold" color={amountColor}>
          {formattedMainValue}
        </Typography>
      </Stack>

      <Divider sx={{ my: 1.5 }} />
      {amountLabels ? (
        <Stack direction="row" spacing={3} alignItems="center">
          <Stack direction="row" spacing={0.5} justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {amountLabels.external}
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {numericUtils.formatNumberToBRLCurrency(Number(transaction.external_value))}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {amountLabels.wallet}
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {numericUtils.formatNumberToBRLCurrency(Number(transaction.wallet_value))}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Nenhum valor foi cobrado.
        </Typography>
      )}
    </Box>
  )
}
